import _ from "lodash";
import {useCallback, useMemo} from "react";
import {useSetAtom} from "jotai";
import {useLoadableValueOrDefault} from "./jotaiHooks";
import {activeCompanyFieldsAtom, activeCompanyFieldsLoadable} from "../store/activeCompanyFieldsStore";
import {activeCompanyFarmsAtom, activeCompanyFarmsLoadable} from "../store/activeCompanyFarmsStore";
import {FarmInsightItem, FieldInsightItem} from "../types/fieldInsightItem";
import {Farm} from "../types/domain/farm";
import {Field} from "../types/domain/field";
import {Crop} from "../types/domain/crop";
import {activeFarmFieldsAtom, activeFarmFieldsLoadable} from "../store/activeFarmFieldsStore";
import {FieldStatus, useFieldStatusById} from "./fieldsHooks";
import {getLatestCropId} from "../utils/fieldUtils";
import {useCrops} from "./cropsHooks";

export const useFarmsInsightsItems = () => {
    const {cropsById} = useCrops();
    const fields = useLoadableValueOrDefault(activeCompanyFieldsLoadable, [], (atom) => atom.fields);
    const farms = useLoadableValueOrDefault(activeCompanyFarmsLoadable, [], (atom) => atom.farms);
    const statusByFieldId = useFieldStatusById();
    const items = useMemo(
        () => farms.map((farm) => mapFarmToFarmInsightItem(farm, fields, cropsById, statusByFieldId)),
        [farms, fields, cropsById, statusByFieldId],
    );
    const refreshFields = useSetAtom(activeCompanyFieldsAtom);
    const refreshFarms = useSetAtom(activeCompanyFarmsAtom);

    const refresh = useCallback(() => {
        refreshFields();
        refreshFarms();
    }, [refreshFields, refreshFarms]);

    return {items, refresh};
};

export const useFieldsInsightsItems = () => {
    const {cropsById} = useCrops();
    const fields = useLoadableValueOrDefault(activeFarmFieldsLoadable, []);
    const statusByFieldId = useFieldStatusById();
    const items = useMemo(
        () => fields.map((field) => mapFieldToFieldInsightItem(field, cropsById, statusByFieldId)),
        [fields, cropsById, statusByFieldId],
    );
    const refreshFields = useSetAtom(activeFarmFieldsAtom);

    const refresh = useCallback(() => {
        refreshFields();
    }, [refreshFields]);

    return {items, refresh};
};

export const useMergedFieldsInsightsItems = (fields: Field[]): FieldInsightItem[] => {
    const {cropsById} = useCrops();
    const statusByFieldId = useFieldStatusById();

    return fields.map((field) => mapFieldToFieldInsightItem(field, cropsById, statusByFieldId));
};

const mapFarmToFarmInsightItem = (
    farm: Farm,
    fields: Field[],
    cropsMap: Record<string, Crop>,
    statusByFieldId: Record<string, FieldStatus>,
): FarmInsightItem => {
    const fieldItems = fields
        .filter((field) => field.farmId === farm.id)
        .map((field) => mapFieldToFieldInsightItem(field, cropsMap, statusByFieldId));

    return {
        type: "FARM",
        name: farm.name,
        moistureAlerts: fieldItems.filter((item) => item.isMoistureAlert).length,
        growthAlerts: fieldItems.filter((item) => item.isGrowthAlert).length,
        crops: _.uniqBy(fieldItems, "crop")
            .map((item) => item.crop)
            .filter((item) => item) as string[],
        areaInHa: _.sumBy(fieldItems, "areaInHa"),
        fields: fieldItems,
        farm,
    };
};

const mapFieldToFieldInsightItem = (
    field: Field,
    cropsById: Record<string, Crop>,
    statusByFieldId: Record<string, FieldStatus>,
): FieldInsightItem => {
    const cropId = getLatestCropId(field);
    return {
        type: "FIELD",
        name: field.name,
        moisture: statusByFieldId[field.id]?.soilMoisture ?? null,
        isMoistureAlert: statusByFieldId[field.id]?.isSoilMoistureAlert ?? false,
        growth: statusByFieldId[field.id]?.cropGrowth ?? null,
        isGrowthAlert: statusByFieldId[field.id]?.isCropGrowthAlert ?? false,
        crop: cropId ? cropsById[cropId]?.name : undefined,
        areaInHa: field.areaInHa,
        field,
        timeUntilNextIrrigation: statusByFieldId[field.id]?.timeUntilNextIrrigation ?? null,
    };
};
